/**
 * video-quiz.js
 * Handles: sequential video -> question flow, YouTube "must watch to end" gating,
 * client-side scoring, and posting the result to your completion endpoint.
 *
 * SECURITY NOTE: the score computed here is client-side and can be tampered with
 * by anyone editing JS in devtools. For a low-stakes marketing discount this is
 * usually an acceptable risk, but your completion endpoint should still do basic
 * sanity checks (e.g. reject scores above the possible max, rate-limit repeated
 * calls per customer) rather than blindly trusting the payload. If this ever
 * needs to be tamper-proof, the question/answer checking should move server-side.
 */
(function () {
  function loadYouTubeAPI(callback) {
    if (window.YT && window.YT.Player) return callback();
    if (window.__videoQuizYTLoading) {
      window.__videoQuizYTCallbacks = window.__videoQuizYTCallbacks || [];
      window.__videoQuizYTCallbacks.push(callback);
      return;
    }
    window.__videoQuizYTLoading = true;
    window.__videoQuizYTCallbacks = [callback];
    var tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = function () {
      window.__videoQuizYTCallbacks.forEach(function (cb) { cb(); });
    };
  }

  function initQuiz(root) {
    var quizId = root.dataset.quizId;
    var config = (window.videoQuizConfig || {})[quizId] || {};
    var slides = Array.prototype.slice.call(root.querySelectorAll('.video-quiz__slide'));
    var currentIndex = 0;
    var correctCount = 0;
    var players = {};

    function showNextInteraction(slide) {
      var hasQuestion = slide.dataset.hasQuestion === 'true';
      if (hasQuestion) {
        var q = slide.querySelector('.video-quiz__question');
        if (q) q.hidden = false;
      } else {
        var btn = slide.querySelector('.video-quiz__continue');
        if (btn) btn.hidden = false;
      }
    }

    function initPlayer(slide, index) {
      var container = slide.querySelector('[id^="yt-player-"]');
      if (!container || players[index]) return;
      var videoId = container.dataset.videoId;
      players[index] = new YT.Player(container.id, {
        videoId: videoId,
        playerVars: { rel: 0, modestbranding: 1 },
        events: {
          onStateChange: function (e) {
            if (e.data === YT.PlayerState.ENDED) {
              showNextInteraction(slide);
            }
          }
        }
      });
    }

    function updateStepLabel(index) {
      var stepLabel = root.querySelector('.current-step');
      if (stepLabel) stepLabel.textContent = index + 1;
    }

    function goToSlide(index) {
      slides.forEach(function (s, i) { s.hidden = i !== index; });
      currentIndex = index;
      updateStepLabel(index);
      var slide = slides[index];
      var container = slide.querySelector('[id^="yt-player-"]');
      if (container) {
        initPlayer(slide, index);
      } else {
        showNextInteraction(slide);
      }
    }

    function handleOptionClick(e) {
      var btn = e.target.closest('.video-quiz__option');
      if (!btn) return;
      var slide = btn.closest('.video-quiz__slide');
      var options = slide.querySelectorAll('.video-quiz__option');
      options.forEach(function (o) { o.disabled = true; });

      if (btn.dataset.correct === 'true') {
        correctCount++;
        btn.classList.add('is-correct');
      } else {
        btn.classList.add('is-incorrect');
        // reveal the correct one for feedback
        slide.querySelector('[data-correct="true"]').classList.add('is-correct');
      }

      setTimeout(function () {
        var nextIndex = currentIndex + 1;
        if (nextIndex < slides.length) {
          goToSlide(nextIndex);
        } else {
          finishQuiz();
        }
      }, 900);
    }

    function handleBackClick(e) {
      var btn = e.target.closest('.video-quiz__back');
      if (!btn) return;
      var prevIndex = currentIndex - 1;
      if (prevIndex >= 0) {
        goToSlide(prevIndex);
      }
    }

    function handleContinueClick(e) {
      var btn = e.target.closest('.video-quiz__continue');
      if (!btn) return;
      var nextIndex = currentIndex + 1;
      if (nextIndex < slides.length) {
        goToSlide(nextIndex);
      } else {
        finishQuiz();
      }
    }

    function finishQuiz() {
      var totalQuestions = root.querySelectorAll('.video-quiz__question').length;
      slides.forEach(function (s) { s.hidden = true; });
      var resultEl = root.querySelector('.video-quiz__result');
      var passEl = root.querySelector('.video-quiz__result-pass');
      var failEl = root.querySelector('.video-quiz__result-fail');
      var loggedOutEl = root.querySelector('.video-quiz__result-loggedout');
      var passed = correctCount >= parseInt(config.passThreshold, 10);

      resultEl.hidden = false;

      if (!config.isLoggedIn) {
        loggedOutEl.hidden = false;
        return;
      }

      if (passed) {
        passEl.hidden = false;
        submitCompletion(passed, totalQuestions);
      } else {
        failEl.hidden = false;
      }
    }

    function submitCompletion(passed, totalQuestions) {
      if (!config.endpointUrl) {
        console.warn('Video quiz: no completion endpoint configured');
        return;
      }
      fetch(config.endpointUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          customerId: config.customerId,
          quizId: config.quizId,
          score: correctCount,
          totalQuestions: totalQuestions,
          passed: passed
        })
      }).catch(function (err) {
        console.error('Quiz completion submission failed', err);
      });
    }

    root.addEventListener('click', handleOptionClick);
    root.addEventListener('click', handleContinueClick);
    root.addEventListener('click', handleBackClick);

    var hasAnyVideo = slides.some(function (s) { return !!s.querySelector('[id^="yt-player-"]'); });
    if (hasAnyVideo) {
      loadYouTubeAPI(function () { goToSlide(0); });
    } else {
      goToSlide(0);
    }
  }

  document.querySelectorAll('.video-quiz').forEach(initQuiz);
})();